import { t } from "@lingui/macro";
import {
  CaretDown,
  ChartBarHorizontal,
  CheckCircle,
  CircleNotch,
  Lightbulb,
  Scissors,
  SmileyWink,
  Sparkle,
} from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useState } from "react";

import { useTokenModal } from "../contexts/token-modal-context";
import { toast } from "../hooks/use-toast";
import { useCentralizedTokens } from "../hooks/use-centralized-tokens";
import { addMetrics } from "../services/openai/add-metrics";
import { changeTone } from "../services/openai/change-tone";
import { fixGrammar } from "../services/openai/fix-grammar";
import { improveWriting } from "../services/openai/improve-writing";
import { makeConcise } from "../services/openai/make-concise";
import { useOpenAiStore } from "../stores/openai";
import { trackAIEvent } from "../utils/gtm";

type Action = "improve" | "fix" | "tone" | "metrics" | "concise";
type Mood = "casual" | "professional" | "confident" | "friendly";

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

const CostBadge = ({ cost }: { cost: number }) => (
  <span className="rounded-full bg-gray-100 px-[5px] py-[1.5px] font-mono text-[9.5px] font-bold text-gray-500">
    {cost}
  </span>
);

export const AiActions = ({ value, onChange, className }: Props) => {
  const [loading, setLoading] = useState<Action | false>(false);
  const { apiKey, useSystemKey } = useOpenAiStore();
  const { tokenBalance, refreshTokens } = useCentralizedTokens();
  const { showTokenModal } = useTokenModal();
  const aiEnabled = !!apiKey || useSystemKey;

  if (!aiEnabled) return null;

  const getTokenCost = (action: Action): number => {
    switch (action) {
      case "improve": return TOKEN_COSTS.IMPROVE_WRITING;
      case "fix": return TOKEN_COSTS.FIX_SPELLING_GRAMMAR;
      case "tone": return TOKEN_COSTS.CHANGE_TONE;
      case "metrics": return TOKEN_COSTS.ADD_METRICS;
      case "concise": return TOKEN_COSTS.MAKE_CONCISE;
      default: return 0;
    }
  };

  const getActionName = (action: Action): string => {
    switch (action) {
      case "improve": return t`Improve Writing`;
      case "fix": return t`Fix Spelling & Grammar`;
      case "tone": return t`Change Tone`;
      case "metrics": return t`Add Metrics`;
      case "concise": return t`Make Concise`;
      default: return "";
    }
  };

  const onClick = async (action: Action, mood?: Mood) => {
    const tokenCost = getTokenCost(action);
    const actionName = getActionName(action);

    if (tokenBalance < tokenCost) {
      showTokenModal({
        requiredTokens: tokenCost,
        currentTokens: tokenBalance,
        featureName: actionName,
      });
      return;
    }

    setLoading(action);

    try {
      let apiResult = value;

      if (action === "improve") {
        apiResult = await improveWriting(value);
        trackAIEvent.improveWriting();
      }
      if (action === "fix") {
        apiResult = await fixGrammar(value);
        trackAIEvent.fixSpellingGrammar();
      }
      if (action === "tone" && mood) {
        apiResult = await changeTone(value, mood);
        trackAIEvent.changeTone(mood);
      }
      if (action === "metrics") {
        apiResult = await addMetrics(value);
      }
      if (action === "concise") {
        apiResult = await makeConcise(value);
      }

      onChange(apiResult);

      toast({
        variant: "default",
        title: t`AI Enhancement Complete`,
        description: t`${actionName} completed. ${tokenCost} tokens used.`,
      });

      await refreshTokens();
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  const chipClass = cn(
    "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white px-[10px] py-[6px]",
    "text-[12px] font-semibold text-gray-700 transition-colors",
    "hover:border-[#268599] hover:text-[#268599]",
    "disabled:cursor-not-allowed disabled:opacity-50",
  );

  const disabled = !!loading || !value.trim();

  return (
    <div
      className={cn(
        "border-t border-dashed border-gray-200 bg-gradient-to-r from-[rgba(38,133,153,0.06)] to-[rgba(232,136,94,0.06)] px-3 pb-3 pt-2.5",
        className,
      )}
    >
      {/* Identity row */}
      <div className="mb-2 flex items-center gap-2">
        <div className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#155469] to-[#E8885E]">
          <Sparkle size={11} weight="fill" className="text-white" />
        </div>
        <span className="text-[12px] font-bold text-[#155469]">{t`AI Writer`}</span>
        <span className="ml-auto rounded-full bg-white/80 px-2 py-0.5 font-mono text-[10px] font-semibold text-gray-500 ring-1 ring-gray-200">
          {tokenBalance} {t`tokens`}
        </span>
      </div>

      {/* Chip row */}
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onClick("improve")}
          className={chipClass}
        >
          {loading === "improve" ? (
            <CircleNotch className="animate-spin" size={12} />
          ) : (
            <Lightbulb weight="fill" className="text-amber-500" size={12} />
          )}
          <span>{t`Improve Writing`}</span>
          <CostBadge cost={TOKEN_COSTS.IMPROVE_WRITING} />
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onClick("fix")}
          className={chipClass}
        >
          {loading === "fix" ? (
            <CircleNotch className="animate-spin" size={12} />
          ) : (
            <CheckCircle weight="fill" className="text-green-500" size={12} />
          )}
          <span>{t`Fix Spelling`}</span>
          <CostBadge cost={TOKEN_COSTS.FIX_SPELLING_GRAMMAR} />
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onClick("metrics")}
          className={chipClass}
        >
          {loading === "metrics" ? (
            <CircleNotch className="animate-spin" size={12} />
          ) : (
            <ChartBarHorizontal weight="fill" className="text-orange-500" size={12} />
          )}
          <span>{t`Add Metrics`}</span>
          <CostBadge cost={TOKEN_COSTS.ADD_METRICS} />
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onClick("concise")}
          className={chipClass}
        >
          {loading === "concise" ? (
            <CircleNotch className="animate-spin" size={12} />
          ) : (
            <Scissors weight="fill" className="text-pink-500" size={12} />
          )}
          <span>{t`Make Concise`}</span>
          <CostBadge cost={TOKEN_COSTS.MAKE_CONCISE} />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" disabled={disabled} className={chipClass}>
              {loading === "tone" ? (
                <CircleNotch className="animate-spin" size={12} />
              ) : (
                <SmileyWink weight="fill" className="text-blue-500" size={12} />
              )}
              <span>{t`Change Tone`}</span>
              <CostBadge cost={TOKEN_COSTS.CHANGE_TONE} />
              <CaretDown size={10} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-32">
            <DropdownMenuItem className="flex items-center" onClick={() => onClick("tone", "casual")}>
              <SmileyWink size={15} weight="fill" className="me-2 text-amber-500" />
              <span>{t`Casual`}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="flex items-center" onClick={() => onClick("tone", "professional")}>
              <span role="img" aria-label={t`Professional`} className="me-2">💼</span>
              <span>{t`Professional`}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="flex items-center" onClick={() => onClick("tone", "confident")}>
              <span role="img" aria-label={t`Confident`} className="me-2">😎</span>
              <span>{t`Confident`}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="flex items-center" onClick={() => onClick("tone", "friendly")}>
              <span role="img" aria-label={t`Friendly`} className="me-2">😊</span>
              <span>{t`Friendly`}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
